/**
 * Where to land after signing in.
 *
 * The protected routes send a signed-out visitor to `/login?redirect=<where
 * they were>`. Only same-app paths are honoured: anything else (`//evil.com`,
 * `https://…`, `/\evil.com`) falls back to the panel, so the parameter cannot
 * be used to bounce someone to another site after login.
 *
 * Google sign-in leaves the app and comes back through `/auth/callback`, so
 * the destination is kept in sessionStorage across that round trip instead of
 * in the OAuth redirect URL (which Supabase checks against an allow-list).
 */

const DEFAULT_DESTINATION = '/admin';
const STORAGE_KEY = 'u360:login-redirect';

export function safeRedirect(value: string | null | undefined): string {
  if (!value || !value.startsWith('/') || value.startsWith('//') || value.startsWith('/\\')) {
    return DEFAULT_DESTINATION;
  }
  return value;
}

/** `/login` with the current location as the place to come back to. */
export function loginPathFor(location: { pathname: string; search: string; hash: string }): string {
  const here = `${location.pathname}${location.search}${location.hash}`;
  if (here === DEFAULT_DESTINATION || here === '/') return '/login';
  return `/login?redirect=${encodeURIComponent(here)}`;
}

export function rememberLoginRedirect(value: string): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, value);
  } catch {
    // Without storage Google sign-in lands on the panel, as before.
  }
}

/** Reads and clears the destination saved before an OAuth round trip. */
export function takeLoginRedirect(): string | null {
  try {
    const value = sessionStorage.getItem(STORAGE_KEY);
    sessionStorage.removeItem(STORAGE_KEY);
    return value;
  } catch {
    return null;
  }
}
